import {
  createContext,
  useContext,
  useEffect,
  useState,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
} from 'react'
import { SettingsService } from '@/services/settings-service'
import { SettingsState, DEFAULT_SETTINGS } from '@/types/settings'
import { logInfo, logError } from '@/lib/logger'
import { themeService, Theme } from '@/services/theme-service'
import { authService } from '@/services/auth-service'
import i18n from '@/i18n/config'
import { changedSettings } from '@/lib/settings-diff'
import { deriveFillTokens, readableAccentText } from '@/lib/theme-fill'
import { useTheme } from '@/hooks/useTheme'

interface SettingsContextType {
  settings: SettingsState
  currentTheme: Theme | null
  isLoading: boolean
  /** The settings came from the backend; until then `saveSettings` writes nothing (#908). */
  hasLoaded: boolean
  isSaving: boolean
  saveSettings: (settings: SettingsState) => Promise<boolean>
  resetSettings: () => Promise<boolean>
  refreshSettings: () => Promise<void>
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined)

interface SettingsProviderProps {
  children: ReactNode
}

// Custom Event Namen zentral
const SETTINGS_UPDATED = 'settings-updated'
const SETTINGS_CONTEXT_SOURCE = 'settings-context'
const USER_LOGGED_OUT = 'user-logged-out'

/**
 * Globaler Settings-Provider
 * Lädt Settings beim App-Start und stellt sie allen Komponenten zur Verfügung
 */
export function SettingsProvider({ children }: SettingsProviderProps) {
  const [settings, setSettings] = useState<SettingsState>(DEFAULT_SETTINGS)
  const [currentTheme, setCurrentTheme] = useState<Theme | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [hasLoaded, setHasLoaded] = useState(false)
  const isMountedRef = useRef(true)
  const settingsRef = useRef(settings)
  settingsRef.current = settings
  // Erst nach einem erfolgreichen Backend-Load darf gespeichert werden (#908).
  const hasLoadedRef = useRef(false)

  // Helper to convert hex to HSL format for Tailwind
  const hexToHSL = useCallback((hex: string): string => {
    // Remove # if present
    hex = hex.replace('#', '')

    // Convert to RGB
    const r = parseInt(hex.substring(0, 2), 16) / 255
    const g = parseInt(hex.substring(2, 4), 16) / 255
    const b = parseInt(hex.substring(4, 6), 16) / 255

    const max = Math.max(r, g, b)
    const min = Math.min(r, g, b)
    const delta = max - min

    let h = 0
    let s = 0
    const l = (max + min) / 2

    if (delta !== 0) {
      s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min)

      switch (max) {
        case r:
          h = ((g - b) / delta + (g < b ? 6 : 0)) / 6
          break
        case g:
          h = ((b - r) / delta + 2) / 6
          break
        case b:
          h = ((r - g) / delta + 4) / 6
          break
      }
    }

    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`
  }, [])

  // Helper to adjust color brightness
  const adjustBrightness = useCallback((hex: string, percent: number): string => {
    const num = parseInt(hex.replace('#', ''), 16)
    const r = Math.max(0, Math.min(255, (num >> 16) + percent))
    const g = Math.max(0, Math.min(255, ((num >> 8) & 0x00FF) + percent))
    const b = Math.max(0, Math.min(255, (num & 0x0000FF) + percent))
    return `#${((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1)}`
  }, [])

  // Helper function for deep merging settings to ensure new properties are added
  const deepMergeSettings = (loaded: Partial<SettingsState>): SettingsState => {
    return {
      ...DEFAULT_SETTINGS,
      ...loaded,
      background: {
        ...DEFAULT_SETTINGS.background,
        ...(loaded.background || {}),
      },
      desktopIcons: {
        ...DEFAULT_SETTINGS.desktopIcons,
        ...(loaded.desktopIcons || {}),
      },
      driveOnlyMode: {
        ...DEFAULT_SETTINGS.driveOnlyMode,
        ...(loaded.driveOnlyMode || {}),
      },
      taskbar: {
        ...DEFAULT_SETTINGS.taskbar,
        ...(loaded.taskbar || {}),
      },
      changelog: {
        ...DEFAULT_SETTINGS.changelog,
        ...(loaded.changelog || {}),
      },
    };
  }

  const applyLoaded = useCallback((loaded: SettingsState) => {
    const merged = deepMergeSettings(loaded)
    settingsRef.current = merged
    setSettings(merged)
    hasLoadedRef.current = true
    setHasLoaded(true)
  }, [])

  /**
   * Lädt Settings aus dem Backend. Scheitert das, bleiben die Standardwerte
   * nur zur Anzeige stehen — gespeichert wird dann nichts, bis ein Load
   * gelungen ist (#908).
   */
  const loadSettings = useCallback(async () => {
    try {
      setIsLoading(true)
      const loaded = await SettingsService.loadUserSettings()
      if (isMountedRef.current) applyLoaded(loaded)
    } catch (error) {
      logError(error as Error, {
        context: 'Globale Settings laden',
        fallback: 'Standardwerte nur angezeigt, Speichern gesperrt',
      })
    } finally {
      if (isMountedRef.current) setIsLoading(false)
    }
  }, [applyLoaded])

  const reloadFromBackend = useCallback(async () => {
    try {
      const loaded = await SettingsService.loadUserSettings()
      if (isMountedRef.current) applyLoaded(loaded)
    } catch (error) {
      logError(error as Error, { context: 'Settings nach Update neu laden' })
    }
  }, [applyLoaded])

  /**
   * Speichert die Keys, die sich gegenüber dem aktuellen Stand geändert haben,
   * und informiert andere Komponenten.
   *
   * Aufrufer übergeben den kompletten Stand (`{...settings, x}`). Als PUT
   * hätte ein veralteter oder noch nicht geladener Stand alles andere
   * überschrieben — deshalb nur der Diff per PATCH, und vor dem ersten
   * erfolgreichen Load gar nichts.
   */
  const saveSettings = useCallback(async (newSettings: SettingsState): Promise<boolean> => {
    if (!hasLoadedRef.current) {
      logError(new Error('Settings nicht aus dem Backend geladen, Speichern verworfen'), {
        context: 'Settings speichern',
        keys: Object.keys(changedSettings(settingsRef.current, newSettings)),
      })
      return false
    }

    const changes = changedSettings(settingsRef.current, newSettings)
    settingsRef.current = newSettings
    setSettings(newSettings)
    if (Object.keys(changes).length === 0) return true

    try {
      setIsSaving(true)
      const success = await SettingsService.patchSettings(changes)
      if (success) {
        window.dispatchEvent(new CustomEvent(SETTINGS_UPDATED, { detail: { source: SETTINGS_CONTEXT_SOURCE } }))
      } else {
        // Der State zeigt sonst etwas, das nicht in Postgres steht.
        void reloadFromBackend()
      }
      return success
    } finally {
      setIsSaving(false)
    }
  }, [reloadFromBackend])

  /**
   * Setzt Settings auf Standardwerte zurück
   */
  const resetSettings = useCallback(async (): Promise<boolean> => {
    return await saveSettings(DEFAULT_SETTINGS)
  }, [saveSettings])

  /**
   * Lädt Settings neu
   */
  const refreshSettings = useCallback(async (): Promise<void> => {
    await loadSettings()
  }, [loadSettings])

  // Settings beim App-Start laden und Mounted-State pflegen
  useEffect(() => {
    isMountedRef.current = true
    loadSettings()
    return () => {
      isMountedRef.current = false
    }
  }, [loadSettings])

  useEffect(() => {
    if (isLoading || !settings.language || i18n.language === settings.language) {
      return
    }

    logInfo(`Synchronisiere i18n-Sprache aus SettingsContext: ${settings.language}`)
    void i18n.changeLanguage(settings.language)
  }, [isLoading, settings.language])

  const [themeUpdateTrigger, setThemeUpdateTrigger] = useState(0)

  // Load theme details when theme_id changes AND settings are fully loaded
  useEffect(() => {
    const loadThemeDetails = async () => {
      // Don't load theme during initial loading phase
      if (isLoading) {
        return
      }

      // Don't load theme if user is not authenticated (backend requires auth)
      const isAuthenticated = await authService.isAuthenticated()
      if (!isAuthenticated) {
        logInfo('Skipping theme load - user not authenticated')
        return
      }

      let theme: Theme | null = null

      if (!settings.theme_id) {
        // No theme_id set - try to load light theme as fallback
        try {
          theme = await themeService.getThemeByName('light')
        } catch (error) {
          logError(error as Error, { context: 'Loading fallback light theme' })
        }
      } else {
        try {
          theme = await themeService.getThemeById(settings.theme_id)
        } catch (error) {
          logError(error as Error, { context: 'Loading theme details' })
          // Try fallback to light theme
          try {
            theme = await themeService.getThemeByName('light')
          } catch (fallbackError) {
            logError(fallbackError as Error, { context: 'Loading fallback theme' })
          }
        }
      }

      if (theme) {
        setCurrentTheme(theme)

        // Sync background image from theme to settings.
        // This ensures the desktop background updates when theme is changed
        // externally (e.g., from the document editor) where only theme_id is
        // PATCHed without updating background.image.
        const newImage = theme.background_image_url || settingsRef.current.background?.image
        if (newImage && newImage !== settingsRef.current.background?.image) {
          const updatedSettings: SettingsState = {
            ...settingsRef.current,
            background: {
              ...settingsRef.current.background,
              image: newImage,
              imageMode: 'cover' as const,
            },
          }
          saveSettings(updatedSettings)
        }
      }
    }

    loadThemeDetails()
  }, [settings.theme_id, isLoading, themeUpdateTrigger, saveSettings])

  // Die Theme-Klasse kommt aus der DB. `cdb-theme` in localStorage, das
  // `ThemeProvider` beim Start liest, ist nur noch der Stand für den ersten
  // Frame — vorher war es die einzige Quelle der Klasse (#908).
  const { theme: activeThemeClass, setTheme } = useTheme()
  const setThemeRef = useRef(setTheme)
  setThemeRef.current = setTheme
  useEffect(() => {
    // Nach einem gescheiterten Load ist `currentTheme` nur der Fallback —
    // der soll den zuletzt bestätigten Stand nicht verdrängen.
    if (!currentTheme || !hasLoadedRef.current) return
    if (currentTheme.css_class !== activeThemeClass) {
      setThemeRef.current(currentTheme.css_class)
    }
  }, [currentTheme, activeThemeClass])

  // Apply theme accent color as CSS variable
  useEffect(() => {
    if (!currentTheme) return

    const root = document.documentElement
    const body = document.body
    const accentColor = currentTheme.accent_color || '#4F8BFF'


    // Helper to set variable on both root and body to ensure override
    const setVar = (name: string, value: string) => {
      root.style.setProperty(name, value)
      body.style.setProperty(name, value)
    }

    // Set CSS variable for interactive elements
    setVar('--theme-interactive-color', accentColor)


    // CRITICAL: Override Tailwind's --primary variable (HSL format)
    const hsl = hexToHSL(accentColor)
    setVar('--primary', hsl)
    setVar('--accent', hsl)
    setVar('--ring', hsl)

    // Apply custom background color - ALWAYS set for consistency
    const bgColor = currentTheme.background_color || (currentTheme.is_dark_mode ? '#1a1a2e' : '#FFFFFF')
    setVar('--cdx-color-bg-base', bgColor)
    setVar('--color-window-bg', bgColor)
    setVar('--window-background', bgColor)
    setVar('--app-bg', bgColor)
    setVar('--surface-primary', bgColor)
    setVar('--surface-secondary', bgColor)
    setVar('--input-bg', bgColor)  // Used by Input component
    // Popover/menu surface. A custom theme replaces `dark` on <html> rather than
    // adding to it, so the `.dark` block in index.css no longer applies and
    // `--menu-bg` would fall back to its light `:root` value — white text on a
    // white menu (#543). Everything reading it must therefore be set here.
    setVar('--menu-bg', bgColor)
    setVar('--sidebar-background', bgColor)

    // Apply sidebar hover effect - slightly darker/lighter than background
    const sidebarHoverColor = adjustBrightness(bgColor, currentTheme.is_dark_mode ? 15 : -10)
    setVar('--sidebar-hover', sidebarHoverColor)
    setVar('--menu-item-hover', sidebarHoverColor)

    // Set background HSL for Tailwind bg-background class
    const bgHsl = hexToHSL(bgColor)
    setVar('--background', bgHsl)

    // Set card background for Card component
    setVar('--card', bgHsl)  // Card background (same as normal background)

    // Apply taskbar color (separate from general background)
    const taskbarColor = currentTheme.taskbar_color || bgColor
    setVar('--color-taskbar-bg', taskbarColor)
    setVar('--taskbar-bg', taskbarColor)

    // Apply titlebar color (separate from general background)
    const titlebarColor = currentTheme.titlebar_color || bgColor
    setVar('--color-window-titlebar-bg', titlebarColor)
    setVar('--window-titlebar-bg', titlebarColor)

    // Apply custom text color - ALWAYS set for consistency
    const txtColor = currentTheme.text_color || (currentTheme.is_dark_mode ? '#FFFFFF' : '#000000')
    setVar('--text-color', txtColor)
    setVar('--cdx-color-text-high', txtColor)
    setVar('--color-window-titlebar-text', txtColor)
    const textHsl = hexToHSL(txtColor)
    setVar('--foreground', textHsl)
    setVar('--card-foreground', textHsl)  // Card text color

    // Apply secondary text color for placeholders, labels, etc.
    const secondaryTxtColor = currentTheme.secondary_text_color || (currentTheme.is_dark_mode ? '#9CA3AF' : '#6B7280')
    setVar('--text-secondary', secondaryTxtColor)
    setVar('--cdx-color-text-low', secondaryTxtColor)
    setVar('--color-placeholder', secondaryTxtColor)
    setVar('--placeholder-color', secondaryTxtColor)

    // Apply border color for UI elements (sidebars, cards, filters, etc.)
    const borderColor = currentTheme.border_color || (currentTheme.is_dark_mode ? '#374151' : '#E5E7EB')
    setVar('--border-color', borderColor)
    setVar('--color-border', borderColor)
    setVar('--sidebar-border', borderColor)
    setVar('--card-border', borderColor)
    setVar('--input-border', borderColor)
    setVar('--filter-border', borderColor)
    setVar('--window-border-color', borderColor)  // Used in AppStoreApp and other components
    const borderHsl = hexToHSL(borderColor)
    setVar('--border', borderHsl)  // Tailwind border-border class
    setVar('--input', borderHsl)   // Tailwind border-input class (used by Select component)

    // Filled controls (primary/danger button, checked toggle): hover, press and
    // a text colour picked from the theme's own text/background colour so it
    // stays readable on any accent — white on a yellow accent is not (#1127).
    const applyFill = (prefix: string, fill: string) => {
      const tokens = deriveFillTokens(fill, [txtColor, bgColor])
      if (!tokens) {
        // Not a hex colour: fall back to the stylesheet defaults instead of
        // keeping the previous theme's values.
        for (const suffix of ['-hover', '-active', '-foreground', '-knob']) {
          root.style.removeProperty(prefix + suffix)
          body.style.removeProperty(prefix + suffix)
        }
        return
      }
      setVar(`${prefix}-hover`, tokens.hover)
      setVar(`${prefix}-active`, tokens.active)
      setVar(`${prefix}-foreground`, tokens.foreground)
      setVar(`${prefix}-knob`, tokens.knob)
    }
    applyFill('--theme-interactive', accentColor)
    applyFill('--theme-danger', getComputedStyle(root).getPropertyValue('--cdx-color-danger').trim())
    const accentText = readableAccentText(accentColor, bgColor, txtColor)
    if (accentText) {
      setVar('--theme-interactive-text', accentText)
    } else {
      root.style.removeProperty('--theme-interactive-text')
      body.style.removeProperty('--theme-interactive-text')
    }

    // Light neutral for player chrome on a dark video scrim (Slider overlay variant).
    setVar('--theme-neutral-50', '#FFFFFF')

  }, [currentTheme, hexToHSL, adjustBrightness])

  // Event Listener für Settings-Updates
  useEffect(() => {
    // Andere Stellen (Ordner, Widgets, Desktop-Apps) schreiben per PATCH
    // direkt ins Backend. Neu laden, damit der nächste Save nicht auf einem
    // veralteten Stand aufsetzt. Eigene Saves sind schon im State.
    const onSettingsUpdate = (ev: Event) => {
      if ((ev as CustomEvent).detail?.source === SETTINGS_CONTEXT_SOURCE) return
      void reloadFromBackend()
    }

    // Auf Logout reagieren
    const onUserLoggedOut = () => {
      hasLoadedRef.current = false
      if (isMountedRef.current) setHasLoaded(false)
      logInfo('User-Logout Event empfangen, setze Settings zurueck...')
      if (isMountedRef.current) setSettings(DEFAULT_SETTINGS)
    }

    // Auf Theme-Update reagieren (wenn Theme-Definition geändert wurde)
    const onThemeUpdated = () => {
      logInfo('Theme-Update Event empfangen, lade Theme neu...')
      // Force re-run of theme loading by toggling a trigger or calling the loader directly
      // Since we can't easily toggle a dependency here without state, we'll duplicate the load logic slightly
      // or better: use a state counter
      setThemeUpdateTrigger(prev => prev + 1)
    }

    window.addEventListener(SETTINGS_UPDATED, onSettingsUpdate)
    window.addEventListener(USER_LOGGED_OUT, onUserLoggedOut)
    window.addEventListener('theme-updated', onThemeUpdated)

    return () => {
      window.removeEventListener(SETTINGS_UPDATED, onSettingsUpdate)
      window.removeEventListener(USER_LOGGED_OUT, onUserLoggedOut)
      window.removeEventListener('theme-updated', onThemeUpdated)
    }
  }, [reloadFromBackend])

  const value: SettingsContextType = useMemo(
    () => ({
      settings,
      currentTheme,
      isLoading,
      hasLoaded,
      isSaving,
      saveSettings,
      resetSettings,
      refreshSettings,
    }),
    [settings, currentTheme, isLoading, hasLoaded, isSaving, saveSettings, resetSettings, refreshSettings],
  )

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>
}

/**
 * Hook zum Verwenden der Settings
 */
export function useSettingsContext(): SettingsContextType {
  const context = useContext(SettingsContext)
  if (context === undefined) {
    throw new Error('useSettingsContext muss innerhalb eines SettingsProvider verwendet werden')
  }
  return context
}


/**
 * The same context, but null instead of a throw without a provider — for UI
 * that also renders outside the protected desktop (and in component tests)
 * and can fall back to defaults.
 */
export function useSettingsContextOptional(): SettingsContextType | null {
  return useContext(SettingsContext) ?? null
}
